"use client";

/**
 * The detail-modal / detail-page **History tab** — the second entry point to the
 * audit feed (plan → "UI specification": *"The same components serve the P4
 * detail-modal History tab — one implementation, two entry points."*).
 *
 * It is a thin panel on purpose. All behaviour — paging, expansion, the two-step
 * restore, the confirmation — is `EntityAuditFeed`, exactly as used by the
 * place-modal. The only things this adds are the tab's own framing and, more
 * importantly, the EMPTY-PARENT GUARD below.
 *
 * ## Why the guard is here and not only in the feed
 *
 * DETAIL_MODAL_TAB_COVERAGE names two invisible failure modes for detail tabs.
 * The second is a panel whose scoping id is missing rendering UNSCOPED — for a
 * nested table that means every row in the tenant under a heading naming one
 * record. The audit read is already id-scoped in its route, so there is no
 * tenant-wide read to leak; but a tab that silently renders an empty feed for a
 * record it could not identify is the same class of lie, and the established
 * pattern for it in this codebase (`ContactDeals`, `CompanyContacts`, the
 * contracts tabs) is an EARLY RETURN with an explanatory empty state. That is
 * what this does, at the tab boundary where a reviewer looks for it. The feed
 * repeats the check as defence in depth — and, crucially, its read hook is
 * `enabled: Boolean(entityId)`, so no request is issued either way.
 */

import React from "react";
import { History } from "lucide-react";

import type { AuditableEntityType } from "@/lib/audit/api-contract";

import { EntityAuditFeed } from "./EntityAuditFeed";

export interface EntityAuditHistoryTabProps {
  entityType: AuditableEntityType;
  /**
   * The parent record's id. Typed to admit the absent cases the codegen types
   * claim cannot happen: a detail surface can be handed a partially selected
   * item, and `id` being present in the TYPE is not proof it is present in the
   * VALUE.
   */
  entityId: string | null | undefined;
  /** The record's own name, shown in the restore confirmation. */
  subjectLabel: string;
  /** Singular entity noun for the empty state ("contact", "company"). */
  entityNoun: string;
}

export function EntityAuditHistoryTab({
  entityType,
  entityId,
  subjectLabel,
  entityNoun,
}: EntityAuditHistoryTabProps) {
  const scopedId = typeof entityId === "string" && entityId.length > 0 ? entityId : null;

  if (scopedId === null) {
    return (
      <div
        data-testid="audit-history-tab-no-parent"
        className="mt-4 flex flex-col items-center gap-2 rounded-lg border border-border p-6 text-center md:p-8"
      >
        <History aria-hidden="true" className="size-6 text-muted-foreground" />
        <h3 className="text-base font-semibold text-card-foreground md:text-lg">
          No history to show
        </h3>
        <p className="text-sm text-muted-foreground">
          This {entityNoun} has no id on the record that was loaded, so there is no history to
          read. Reopen it from the {entityNoun} list and try again.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-3">
      <p className="text-sm text-muted-foreground">
        Every recorded change to this {entityNoun}, newest first. Restoring appends a new entry —
        history is never rewritten.
      </p>
      <div className="rounded-lg border border-border">
        <EntityAuditFeed
          entityType={entityType}
          entityId={scopedId}
          subjectLabel={subjectLabel}
          scrollAreaClassName="max-h-[60vh]"
        />
      </div>
    </div>
  );
}
